import { DateTime } from "luxon";
import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import type { RootState } from "typesafe-actions";
import { createSelectThumbnailUrlByWorkspaceId } from "../../../selectors/selectThumbnailUrlByWorkspaceId";
import type { AppState } from "../../../state/root.reducer";
import type { MyWorkspaceMetadata } from "../../../state/workspaces/types";
import { selectHasModifiedThumbnail } from "../../../state/workspaces/workspaces.selector";
import { useWorkspaceDefaultImage } from "../../../utils/defaultImages";
import WorkspaceImagePreview from "./WorkspaceImagePreview";

export type CustomWorkspacePreviewProps = {
  canEditLabels: boolean;
  myWorkspaceMetadata: MyWorkspaceMetadata;
  setIsVisible: (value: boolean) => void;
  showEditLabelPrompt: () => void;
  showWorkspaceWeight: boolean;
  thumbnailPreviewUrl?: string;
  token: string;
};

const CustomWorkspacePreview = ({
  canEditLabels,
  myWorkspaceMetadata,
  setIsVisible,
  showEditLabelPrompt,
  showWorkspaceWeight,
  thumbnailPreviewUrl,
  token,
}: CustomWorkspacePreviewProps) => {
  const selectThumbnailUrlByWorkspaceId = useMemo(
    createSelectThumbnailUrlByWorkspaceId,
    [],
  );

  const defaultImgSrc = useWorkspaceDefaultImage(
    myWorkspaceMetadata?.workspaceType ?? "HOYLU",
  );

  const workspaceThumbnailData = useSelector((state: AppState) =>
    selectThumbnailUrlByWorkspaceId(state, myWorkspaceMetadata?.workspaceId),
  );
  const ignoreCache = useSelector((state: RootState) =>
    selectHasModifiedThumbnail(state, myWorkspaceMetadata?.workspaceId),
  );

  const [imgSrc, setImgSrc] = useState<string>("");

  useEffect(() => {
    let isRecentlyAccessed = false;
    let customThumbnailUrl =
      thumbnailPreviewUrl || workspaceThumbnailData.thumbnailUrl;

    if (!customThumbnailUrl) return;

    if (myWorkspaceMetadata?.lastAccess) {
      const now = DateTime.utc();
      isRecentlyAccessed = now
        .minus({ hour: 24 })
        .until(now)
        .contains(
          DateTime.fromISO(myWorkspaceMetadata.lastAccess, { zone: "utc" }),
        );
    }

    if (customThumbnailUrl.startsWith("blob:")) {
      setImgSrc(customThumbnailUrl);
    } else {
      if (
        isRecentlyAccessed ||
        ignoreCache ||
        myWorkspaceMetadata?.templateId
      ) {
        customThumbnailUrl += "?defyCache=" + Date.now();
      }
      // fetching here to nicely handle 404's and avoid situation when default thumbnails are blinking
      fetch(customThumbnailUrl, {
        headers: { Authorization: `Bearer ${token}` },
      })
        .then((response: Response) => {
          if (response.status === 200) {
            return response.blob();
          }
          return undefined;
        })
        .then((blob?: Blob) => {
          if (blob instanceof Blob) {
            setImgSrc(URL.createObjectURL(blob));
          }
        })
        .catch(console.debug);
    }
    setIsVisible(true);
  }, [
    ignoreCache,
    thumbnailPreviewUrl,
    workspaceThumbnailData.thumbnailChanged,
  ]);

  useEffect(() => {
    const imgSrcCache = imgSrc;
    return () => {
      URL.revokeObjectURL(imgSrcCache); // Revoke URL when img src change
    };
  }, [imgSrc]);

  return (
    <WorkspaceImagePreview
      canEditLabels={!!canEditLabels}
      imgSrc={imgSrc || defaultImgSrc}
      myWorkspaceMetadata={myWorkspaceMetadata}
      showEditLabelPrompt={showEditLabelPrompt}
      showWorkspaceWeight={showWorkspaceWeight}
    />
  );
};

export default CustomWorkspacePreview;
